// Presentation helpers for the Agent Radar surface. All pure — they turn the API
// entry payload (real player data) into the exact strings/tints the design source
// shows ("LB · 21 · 🇭🇷 CRO", "Contract until Jun 2027", "Added 12 Jul"). Never
// hardcodes the prototype's sample players; every value comes from the entry.

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const norm = (s) => String(s || "").toLowerCase().replace(/[\s_-]+/g, " ").trim();

// Any stored position token (enum key, full label, or short code) → the design's
// short code (LB/CB/ST…). Falls back to an upper-cased raw token so unknown
// positions still render and still filter self-consistently.
const POS_SHORT = {
  goalkeeper: "GK", "goal keeper": "GK", gk: "GK",
  "left back": "LB", lb: "LB",
  "right back": "RB", rb: "RB",
  "centre back": "CB", "center back": "CB", cb: "CB",
  "left centre back": "CB", "right centre back": "CB",
  "defensive midfielder": "DM", dm: "DM", dmf: "DM",
  "central midfielder": "CM", "centre midfielder": "CM", cm: "CM", cmf: "CM",
  "attacking midfielder": "AM", am: "AM", amf: "AM",
  "left winger": "LW", lw: "LW", lwf: "LW",
  "right winger": "RW", rw: "RW", rwf: "RW",
  "centre forward": "ST", "center forward": "ST", striker: "ST", st: "ST", cf: "ST",
};

export const radarShortPosition = (value) => {
  if (!value) return "";
  const hit = POS_SHORT[norm(value)];
  if (hit) return hit;
  return String(value).toUpperCase();
};

// Meta line under the player name: "LB · 21 · 🇭🇷 CRO" — parts dropped when absent.
// Nationality text = every citizenship (L-FE-1) when the radar payload carries `nationalities[]` (names), else the single one.
export const radarNationalityText = (entry = {}) =>
  Array.isArray(entry.nationalities) && entry.nationalities.length
    ? entry.nationalities.filter((n) => typeof n === "string" && n.trim() !== "").join(" / ")
    : entry.nationality;

export const radarMetaLine = (entry = {}) => {
  const pos = radarShortPosition(entry.position);
  return [pos, entry.age, radarNationalityText(entry)].filter((x) => x !== null && x !== undefined && x !== "").join(" · ");
};

const parseDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

// "Jun 2027" — contract-until display. Empty when the field is missing.
export const formatMonthYear = (value) => {
  const d = parseDate(value);
  if (!d) return "";
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

// "12 Jul" — the "Added {date}" chip. Empty when missing.
export const formatDayMonth = (value) => {
  const d = parseDate(value);
  if (!d) return "";
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

// "12 Jul, 09:40" — note timestamp. Empty when missing.
export const formatNoteStamp = (value) => {
  const d = parseDate(value);
  if (!d) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${hh}:${mm}`;
};

// Initials from a name (max two letters), for the avatar / crest fallback.
export const radarInitials = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Deterministic tint palette (design uses per-player hues on the initials chip).
const TINTS = ["#3B5BDB", "#0E7490", "#7C3AED", "#059669", "#DC2626", "#2563EB", "#DB2777", "#EA580C", "#65A30D", "#B45309"];

export const radarTint = (seed) => {
  const s = String(seed || "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return TINTS[h % TINTS.length];
};

// Notes chip text: "1 note" / "3 notes".
export const notesChipText = (count) => `${count} ${count === 1 ? "note" : "notes"}`;

// Count text for the entries-pane header / list header: "6 players".
export const playersCountText = (count) => `${count} ${count === 1 ? "player" : "players"}`;

// Prototype toast: bottom-center dark pill (#111827, r9, 9px 15px) — the design
// source's only toast style. Shared by the radar page and the share sheet so
// success feedback is identical everywhere on the radar surface.
export const RADAR_TOAST_STYLE = {
  background: "#111827",
  color: "#FFFFFF",
  fontSize: "12.5px",
  fontWeight: 600,
  borderRadius: "9px",
  padding: "9px 15px",
};
